export interface Slide {
  id: number
  type: string
  title: string
  content?: any
}

export interface TalkingPoint {
  slideId: number
  points: string[]
  keyMessages: string[]
  timing: string
}

export const slides: Slide[] = [
  {
    id: 1,
    type: 'title',
    title: "Stop Losing Leads While You're in the Field",
    content: {
      subtitle:
        "How to Capture Every Lead with Simple Automation (Even When You're Busy)",
      presenter: 'Viridia Analytics - Yelp Advertising Partner',
      date: 'November 24, 2025',
    },
  },
  {
    id: 2,
    type: 'agenda',
    title: "What We'll Cover Today",
    content: {
      items: [
        {
          time: '0-10 min',
          topic: 'Why Leads Slip Through the Cracks',
          description: 'The real cost of being too busy',
        },
        {
          time: '10-25 min',
          topic: 'The Simple 3-Part System',
          description: 'Auto-responses + booking + one CRM',
        },
        {
          time: '25-35 min',
          topic: 'Real Example: TFP Landscaping',
          description: 'What we did and what happened',
        },
        {
          time: '35-45 min',
          topic: 'Interactive Scenarios (You Participate!)',
          description: 'Real situations, real solutions',
        },
        {
          time: '45-55 min',
          topic: 'How to Set This Up + Resources',
          description: 'DIY guide and optional help',
        },
        {
          time: '55-60 min',
          topic: 'Q&A',
          description: 'Your questions answered',
        },
      ],
    },
  },
  {
    id: 3,
    type: 'problem',
    title: "Why You're Losing Leads",
    content: {
      problems: [
        {
          icon: '📞',
          title: "You Can't Answer While Working",
          description:
            "You're fixing something, driving to a job, or with a client. Your phone rings. By the time you call back hours later, they've moved on.",
          stat: '67% Hire First Responder',
        },
        {
          icon: '📧',
          title: 'Leads Are Scattered Everywhere',
          description:
            "Yelp messages, Google inquiries, texts, emails, voicemails. You lose track of who you responded to and who you didn't.",
          stat: 'Average 5+ Lead Sources',
        },
        {
          icon: '⏰',
          title: 'Inquiries Come After Hours',
          description:
            "Someone needs service at 8pm or on Sunday. You don't see it until Monday morning. They've already called three other businesses.",
          stat: '40% Come After 5pm',
        },
        {
          icon: '🤯',
          title: 'No System = Chaos',
          description:
            "You're trying to remember everything in your head or scribbled in a notebook. Follow-ups get forgotten. Opportunities disappear.",
          stat: '50%+ Never Followed Up',
        },
      ],
    },
  },
  {
    id: 4,
    type: 'solution',
    title: 'The Simple 3-Part System',
    content: {
      steps: [
        {
          number: '01',
          title: 'Auto-Responder',
          description:
            "Every lead gets an instant text or email reply - even when you're busy. It acknowledges their inquiry and sets expectations.",
          outcomes: [
            'Responds in under 60 seconds',
            'Works 24/7, even weekends',
            'Sounds professional and personal',
          ],
        },
        {
          number: '02',
          title: 'Booking Calendar',
          description:
            "Leads can book appointments directly into your calendar. No phone tag. No back-and-forth. They pick a time, it's booked.",
          outcomes: [
            'Eliminates scheduling hassle',
            'Reduces no-shows with reminders',
            'Syncs with your phone calendar',
          ],
        },
        {
          number: '03',
          title: 'Simple CRM',
          description:
            'All your leads flow into one place. You can see every conversation, every appointment, every follow-up needed - all on your phone.',
          outcomes: [
            'One dashboard for everything',
            'Automatic follow-up reminders',
            'Track which leads convert',
          ],
        },
        {
          number: '04',
          title: 'Optional: Yelp Ads',
          description:
            'If you want more leads, Yelp advertising can target customers actively searching for your services. As a Yelp Partner, we can get you discounts and upgrades.',
          outcomes: [
            'Target local customers ready to buy',
            'Get Yelp Partner discounts',
            'Track which ads bring leads',
          ],
        },
      ],
    },
  },
  {
    id: 5,
    type: 'case-study',
    title: 'Real Example: TFP Landscaping',
    content: {
      company: 'TFP Landscaping',
      industry: 'Landscaping & Lawn Care',
      size: 'Small local business, owner + 2 crew members',
      challenge:
        "The owner was constantly in the field doing estimates or managing crews. Leads would call or text, and he couldn't respond for hours. By then, homeowners had already hired someone else. He was also getting Yelp inquiries that he'd forget to check.",
      solution:
        'Set up an auto-responder that instantly texts leads: "Thanks for reaching out! I\'m with a client but will call you back within 2 hours. In the meantime, you can book a free estimate at [link]." Connected Yelp, Google, and phone to one CRM. Added a booking calendar so leads could schedule estimates themselves.',
      results: [
        {
          metric: '3x',
          label: 'More Estimates Booked',
          detail: 'From self-scheduling',
        },
        { metric: '85%', label: 'Lead Response Rate', detail: 'Up from ~40%' },
        {
          metric: '20 min',
          label: 'Average Response Time',
          detail: 'Down from 4+ hours',
        },
        {
          metric: '$31K',
          label: 'Additional Revenue',
          detail: 'First 90 days',
        },
      ],
      testimonial: {
        quote:
          "I was losing so many jobs because I couldn't get to my phone fast enough. Now every lead gets an instant response and most of them just book their own estimate. It's been a game-changer.",
        author: 'Owner',
        title: 'TFP Landscaping',
      },
    },
  },
  {
    id: 6,
    type: 'interactive-scenario',
    title: 'Scenario #1: The Missed Call',
    content: {
      emoji: '🔧',
      scenario: {
        situation:
          "You're a plumber. You're under a kitchen sink fixing a leak - hands covered in pipe dope, water everywhere. Your phone rings. It rings again 20 minutes later. And again an hour later. You finally finish the job 4 hours later and check your phone. Three missed calls from the same number.",
        question:
          'What do you do? How do you handle this? Drop your answer in the chat!',
        withoutSystem: {
          title: 'Without the System',
          outcome:
            'You call back 4 hours later. Voicemail. You try again the next day. No answer. They already hired your competitor who responded in 15 minutes.',
          cost: 'Lost $450 Job',
        },
        withSystem: {
          title: 'With the System',
          outcome:
            'The moment they call, they get an instant text: "Got your message! I\'m on a job but will call you back by 3pm. For emergencies, call [number]. You can also book online at [link]." They feel acknowledged and book online.',
          benefit: 'Job Booked Automatically',
        },
      },
    },
  },
  {
    id: 7,
    type: 'interactive-scenario',
    title: 'Scenario #2: The After-Hours Inquiry',
    content: {
      emoji: '🌙',
      scenario: {
        situation:
          "It's 9pm on a Saturday night. Someone's AC just died and it's 95 degrees outside. They're desperate. They send you a Yelp message and a text. They also message three of your competitors. You're watching a movie with your family and don't check your phone until Sunday morning.",
        question:
          'What happens to this lead? Who gets the job? Share your thoughts in the chat!',
        withoutSystem: {
          title: 'Without the System',
          outcome:
            'You see the message Sunday morning at 10am - 13 hours later. You respond immediately, but they already booked with a competitor who responded at 9:15pm Saturday.',
          cost: 'Lost $2,800 AC Repair',
        },
        withSystem: {
          title: 'With the System',
          outcome:
            'At 9:01pm, they get an instant reply: "Thanks for reaching out! I see you need emergency service. I\'ll call you first thing tomorrow at 8am. For immediate emergencies tonight, call [emergency number]." They wait for you because you responded.',
          benefit: 'You Get the Job',
        },
      },
    },
  },
  {
    id: 8,
    type: 'success-stories',
    title: "What's Possible with This System",
    content: {
      overallStats: [
        {
          value: '< 5 min',
          label: 'Average Response Time',
          sublabel: 'With auto-responder',
        },
        {
          value: '2-3x',
          label: 'More Leads Converted',
          sublabel: 'From faster response',
        },
        {
          value: '50-60%',
          label: 'Self-Book Appointments',
          sublabel: 'Less phone tag',
        },
        {
          value: '10-20',
          label: 'Extra Jobs/Month',
          sublabel: 'From better capture',
        },
      ],
      quickWins: [
        {
          company: 'TFP Landscaping',
          result: '3x more estimates booked, $31K in 90 days',
          industry: 'Real Client Success',
        },
        {
          company: 'Missed Call Scenario',
          result: "Never lose a lead while you're working",
          industry: 'Instant Response',
        },
        {
          company: 'After-Hours Scenario',
          result: 'Capture leads 24/7, even weekends',
          industry: 'Always Available',
        },
        {
          company: 'Any Home Service',
          result: 'Automatic follow-ups, nothing forgotten',
          industry: 'Never Miss Follow-Up',
        },
      ],
    },
  },
  {
    id: 9,
    type: 'implementation',
    title: 'How to Set This Up Yourself',
    content: {
      phases: [
        {
          week: 'Step 1',
          title: 'Choose Your Tools',
          tasks: [
            'Auto-responder: Zapier or Make.com (connects everything)',
            'Booking calendar: Calendly, Acuity, or similar',
            'CRM: GoHighLevel, HubSpot, or simple spreadsheet',
            'Optional: Yelp ads account (we can help with Partner discounts)',
          ],
          deliverable: 'Tools selected and accounts created',
        },
        {
          week: 'Step 2',
          title: 'Set Up Auto-Responses',
          tasks: [
            'Write your response message (keep it simple and friendly)',
            'Connect Zapier to your lead sources (Yelp, Google, phone)',
            'Test it - send yourself a test inquiry',
            'Make sure it triggers within 60 seconds',
          ],
          deliverable: 'Instant responses working',
        },
        {
          week: 'Step 3',
          title: 'Add Booking Calendar',
          tasks: [
            'Set your availability in the calendar tool',
            'Create appointment types (estimate, service call, etc.)',
            'Add the booking link to your auto-response',
            'Test booking an appointment yourself',
          ],
          deliverable: 'Self-scheduling live',
        },
        {
          week: 'Step 4',
          title: 'Connect Your CRM',
          tasks: [
            'Import existing contacts and leads',
            'Connect lead sources to CRM',
            'Set up follow-up reminders',
            'Learn the basics (usually 30 minutes)',
          ],
          deliverable: 'All leads in one place',
        },
      ],
      support: [
        'Free templates and examples',
        'Step-by-step video tutorials',
        'Tool recommendations',
        'Community support group',
      ],
    },
  },
  {
    id: 10,
    type: 'cta',
    title: 'Want Help Setting This Up?',
    content: {
      offers: [
        {
          title: 'Signature Package - $297/month',
          description: 'We set up and manage everything for you',
          value: 'Done-For-You Service',
          included: [
            'Auto-responder system (instant replies 24/7)',
            'Booking calendar integration',
            'White-label CRM (branded for your business)',
            'All lead sources connected in one place',
            'Ongoing support and optimization',
            'Setup fee: $250 (waived for webinar attendees)',
          ],
        },
        {
          title: 'Yelp Ads Management',
          description: 'Get more leads from customers ready to buy',
          value: '10% Retainer Fee',
          included: [
            'Yelp Advertising Partner discounts & upgrades',
            'Campaign setup and optimization',
            'Target local customers actively searching',
            'Monthly performance reporting',
            'Ad spend management',
            'Qualifying businesses get special Yelp perks',
          ],
        },
        {
          title: 'Additional Services',
          description: 'Everything you need to grow online',
          value: 'À La Carte',
          included: [
            'Review generation campaigns',
            'Website design and development',
            'Social media setup (Google, Instagram, Facebook)',
            'Profile optimization (up to 3 platforms)',
            'Local SEO services',
            'Custom packages available',
          ],
        },
      ],
      nextSteps: [
        'Download the free setup guide and templates',
        "Try setting it up yourself (we're here if you need help)",
        'Book a free consultation if you want done-for-you service',
        'Join our community for ongoing tips and support',
      ],
    },
  },
  {
    id: 11,
    type: 'qa',
    title: 'Questions?',
  },
]

export const talkingPoints: TalkingPoint[] = [
  {
    slideId: 1,
    timing: '2 minutes',
    keyMessages: [
      'Introduce yourself and Viridia casually',
      'Set expectation: educational, not a sales pitch',
      'Build rapport with shared experience',
    ],
    points: [
      "Hey everyone, thanks for joining! I'm with Viridia Analytics - we help home service businesses capture more leads and stay organized.",
      "Quick disclaimer: This is NOT a sales pitch. I'm here to teach you a system that works. If you want to set it up yourself, awesome. If you want our help, I'll mention that at the end. No pressure.",
      "Here's why I'm passionate about this: I've seen too many great businesses - plumbers, HVAC techs, landscapers - lose thousands of dollars because they can't respond to leads fast enough.",
      "It's not your fault. You're busy doing the actual work. But there's a simple system that can fix this, and I want to show you exactly how it works.",
      "By the end of this, you'll understand the problem, the solution, and how to implement it - whether you do it yourself or have someone help you.",
      "Sound good? Let's dive in.",
    ],
  },
  {
    slideId: 2,
    timing: '2 minutes',
    keyMessages: [
      'Set clear, educational expectations',
      'Build excitement for interactive scenarios',
      'Emphasize participation',
    ],
    points: [
      "Here's what we're covering today. First, I'll show you why leads slip through the cracks - and it's probably costing you more than you think.",
      "Then I'll walk through the simple 3-part system: auto-responses, booking calendar, and one CRM. Nothing complicated.",
      "We'll look at a real example - TFP Landscaping. I'll show you exactly what we did and what happened. Real numbers.",
      "Then - and this is where it gets fun - we're going to do some interactive scenarios. I'll give you a real situation, you tell me what you'd do, and we'll see how the system handles it.",
      'Make sure you can access the chat because I want to hear from you during these scenarios. This is interactive, not just me talking at you.',
      "At the end, I'll walk through how to set this up yourself and share resources. And if you want help, I'll mention our services. Then we'll do Q&A.",
      "Let's start with the problem, because once you see this, everything else makes sense.",
    ],
  },
  {
    slideId: 3,
    timing: '8 minutes',
    keyMessages: [
      'Make the pain relatable and specific',
      'Use scenarios they experience daily',
      'Quantify the cost of inaction',
    ],
    points: [
      "Let me paint a picture. You're fixing a water heater. Your phone rings. You can't answer - your hands are covered in pipe dope. It rings again 30 minutes later. You're still working.",
      "By the time you finish and check your phone, 4 hours have passed. You call back. Voicemail. They've already hired someone else.",
      "This happens 3-5 times a day for most of you. And here's the brutal truth: 67% of customers hire whoever responds first. Not the best. The FIRST.",
      "But it gets worse. Your leads are coming from everywhere. Yelp messages. Google Business inquiries. Text messages. Phone calls. Emails. You're trying to track it all in your head or a notebook.",
      "And a lot of inquiries come after hours. Someone's AC breaks at 8pm on a Saturday. They reach out to five companies. You don't see it until Monday morning. They've already hired someone by then.",
      "Without a system, you forget to follow up. You lose track of quotes you sent. You miss opportunities because you're human and you're overwhelmed.",
      "The average home service business loses 50% or more of their leads just because they can't respond fast enough or follow up consistently.",
      "If you're getting 20 leads a month and losing half, that's 10 jobs you're not booking. At $500-$1000 per job, that's $5,000-$10,000 per month walking away.",
      "Now, I'm going to show you how to fix this with a simple system.",
    ],
  },
  {
    slideId: 4,
    timing: '12 minutes',
    keyMessages: [
      'Break down the system into simple parts',
      'Explain each component clearly',
      'Show how they work together',
    ],
    points: [
      'The system has three core parts. Let me walk through each one.',
      'Part 1: Auto-responder. This is the game-changer. Every time someone contacts you - Yelp message, text, email, whatever - they get an instant reply.',
      'It happens in under 60 seconds. Even if you\'re on a ladder, under a sink, or driving to a job. The message is simple: "Thanks for reaching out! I\'m with a client but will call you back within 2 hours. You can also book online at [link]."',
      "That's it. But it keeps you top of mind. The lead knows you got their message. They're not wondering if you're ignoring them. And they might just book online instead of waiting.",
      'This works 24/7. Weekend inquiry at 9pm? Instant response. Holiday? Instant response. You\'re never "closed" anymore.',
      'Part 2: Booking calendar. This eliminates phone tag. Leads can see your availability and book appointments themselves.',
      'For landscaping, it might be estimate appointments. For HVAC, it might be service calls. For lawyers, it might be consultations. Whatever makes sense for your business.',
      'The calendar syncs with your phone. You get reminders. It reduces no-shows because it sends automatic reminders to the customer.',
      "Part 3: Simple CRM. This is where all your leads live. Yelp inquiry? It's in there. Google message? In there. Phone call? Logged automatically.",
      'You can see every conversation, every appointment, every follow-up you need to do. All on your phone. No more notebook. No more trying to remember who you talked to.',
      'The CRM can also send automatic follow-ups. If you sent a quote and haven\'t heard back in 3 days, it reminds you to follow up. Or it can send an automatic "just checking in" message.',
      'These three parts work together. Auto-responder catches every lead instantly. Booking calendar lets them schedule themselves. CRM keeps everything organized.',
      "Now, there's an optional fourth part: Yelp advertising. If you want MORE leads, Yelp ads can target customers actively searching for your services.",
      "We're a Yelp Advertising Partner, which means for qualifying businesses, we can get you discounts and free upgrades. But this is separate from the core system.",
      'Let me show you a real example of this in action.',
    ],
  },
  {
    slideId: 5,
    timing: '6 minutes',
    keyMessages: [
      'This is the ONLY real client - be clear about that',
      'Walk through what was done step-by-step',
      'Focus on the transformation - 4 hours to 20 minutes!',
    ],
    points: [
      'TFP Landscaping is a real client. Small local business - owner plus two crew members. Great at landscaping, but struggling with lead response.',
      "The owner was constantly in the field doing estimates or managing crews. When leads would call or text, he couldn't respond for hours. By then, homeowners had already hired someone else.",
      "He was also getting Yelp inquiries that he'd forget to check. He'd remember two days later and the lead was long gone.",
      'Here\'s what we did: First, we set up an auto-responder. Every lead gets an instant text: "Thanks for reaching out! I\'m with a client but will call you back within 2 hours. In the meantime, you can book a free estimate at [link]."',
      "Second, we added a booking calendar. Leads could schedule their own estimate appointments. No phone tag. They pick a time, it's booked.",
      'Third, we connected everything to one CRM. Yelp messages, Google inquiries, phone calls - all flowing into one place. He could see his entire pipeline on his phone.',
      'Results: He went from booking maybe 40% of leads to 85%. His average response time went from 4+ hours to just 20 minutes.',
      "But here's the big one: He booked 3x more estimates because of self-scheduling. People loved being able to just pick a time instead of playing phone tag.",
      'In the first 90 days, he tracked $31,000 in additional revenue directly from better lead capture and follow-up.',
      'He told me: "I was losing so many jobs because I couldn\'t get to my phone fast enough. Now every lead gets an instant response and most of them just book their own estimate. It\'s been a game-changer."',
      "That's a real example. Now let's get interactive. I'm going to give you some scenarios and I want to hear what YOU would do.",
    ],
  },
  {
    slideId: 6,
    timing: '5 minutes',
    keyMessages: [
      'Get attendees engaged in chat',
      'Make them realize they have this problem',
      'Show the dramatic difference',
    ],
    points: [
      "Alright, interactive time! Here's scenario #1. I want you to put yourself in this situation.",
      "[Read the scenario] You're a plumber under a kitchen sink. Phone rings three times over 4 hours. You finally check your phone.",
      "What do you do? How do you handle this? Be honest - drop your answer in the chat. What's your move?",
      '[Pause for chat responses - acknowledge a few] "Yeah, exactly..." "That\'s what most people do..." "Right, you call back..."',
      "Okay, let's see what happens in both scenarios. WITHOUT the system: You call back 4 hours later. Voicemail. You try again. No answer. They already hired someone who responded in 15 minutes. You just lost a $450 job.",
      'WITH the system: The moment they called, they got an instant text. "Got your message! I\'m on a job but will call you back by 3pm. For emergencies, call [number]. You can also book online at [link]."',
      "They feel acknowledged. They know you're coming. They might even book online right then. The job is yours.",
      "See the difference? It's not about being available 24/7. It's about instant acknowledgment. Let's do another one.",
    ],
  },
  {
    slideId: 7,
    timing: '5 minutes',
    keyMessages: [
      'Keep engagement high',
      'Emphasize after-hours problem',
      "Show system works when you're offline",
    ],
    points: [
      "Scenario #2: It's 9pm Saturday night. Someone's AC just died. 95 degrees outside. They're desperate.",
      "They send you a Yelp message and a text. They also message three of your competitors. You're watching a movie with your family. You don't check your phone until Sunday morning.",
      'What happens to this lead? Who gets the job? Drop your thoughts in the chat!',
      '[Pause for responses] "Yep, exactly..." "That\'s the reality..." "Competitor gets it..."',
      'WITHOUT the system: You see the message Sunday at 10am - 13 hours later. You respond immediately, but they already booked with a competitor who responded at 9:15pm.',
      'You just lost a $2,800 AC repair job because you were offline for 13 hours. And you SHOULD be offline on Saturday night with your family!',
      'WITH the system: At 9:01pm, they get an instant reply. "Thanks for reaching out! I see you need emergency service. I\'ll call you first thing tomorrow at 8am. For immediate emergencies tonight, call [emergency number]."',
      "They feel heard. They know you're going to help. They wait for you because you responded. You get the job.",
      "The system works even when you're not working. And by the way, the follow-up problem we talked about earlier? The CRM handles that automatically too. You'll never forget a quote or follow-up again.",
      "Let me show you what's possible when you implement this.",
    ],
  },
  {
    slideId: 8,
    timing: '3 minutes',
    keyMessages: [
      'Summarize the benefits',
      'Reference the scenarios they just experienced',
      'Build confidence this works',
    ],
    points: [
      "Let me summarize what's possible with this system.",
      "Average response time drops to under 5 minutes. Even if you're busy, leads get instant acknowledgment - like in the plumber scenario.",
      'You convert 2-3x more leads just from responding faster. Remember, 67% hire whoever responds first.',
      '50-60% of leads will self-book if you give them the option. That eliminates phone tag.',
      "Most businesses see 10-20 extra jobs per month from better capture and follow-up. That's real revenue.",
      'TFP Landscaping - our real client - booked 3x more estimates and added $31K in 90 days. Response time went from 4 hours to 20 minutes.',
      'The scenarios we just went through? Those are real situations you face every day. The missed call. The after-hours inquiry. The forgotten follow-up.',
      "The system handles all of them automatically. You never lose a lead while you're working. You capture after-hours inquiries. You never forget a follow-up.",
      'Now let me show you how to set this up.',
    ],
  },
  {
    slideId: 9,
    timing: '8 minutes',
    keyMessages: [
      'Give them the actual roadmap to DIY',
      'Be generous with information',
      "Show it's doable but takes work",
    ],
    points: [
      "Alright, here's how to set this up yourself. I'm going to give you the exact steps.",
      'Step 1: Choose your tools. For auto-responder, you need Zapier or Make.com. These connect everything together. They have free plans to start.',
      'For booking calendar, Calendly is popular and easy. Acuity is another good one. For CRM, GoHighLevel is what we use, but HubSpot has a free version. Even a simple spreadsheet works to start.',
      "If you want Yelp ads, you'll need a Yelp ads account. As a Yelp Partner, we can help you get discounts and upgrades if you qualify.",
      'Step 2: Set up auto-responses. Write your message. Keep it simple and friendly. "Thanks for reaching out! I\'m with a client but will call you back within [timeframe]. You can also book online at [link]."',
      'Connect Zapier to your lead sources. Yelp, Google Business, your phone number, email. When a lead comes in, Zapier triggers the auto-response.',
      'Test it. Send yourself a test inquiry. Make sure the response goes out within 60 seconds. Tweak the message until it sounds right.',
      'Step 3: Add your booking calendar. Set your availability. Create appointment types - estimates, service calls, consultations, whatever you need.',
      'Add the booking link to your auto-response message. Test booking an appointment yourself to make sure it works.',
      'Step 4: Connect your CRM. Import your existing contacts and leads. Connect your lead sources so everything flows into the CRM automatically.',
      'Set up follow-up reminders. The CRM should remind you when you need to follow up with a lead or check in on a quote.',
      'Learn the basics. Most CRMs take about 30 minutes to get comfortable with. Watch their tutorial videos.',
      "That's it. Four steps. Is it some work? Yes. But it's doable. And the payoff is huge.",
      'Now, if you want help with this, let me tell you about our services.',
    ],
  },
  {
    slideId: 10,
    timing: '6 minutes',
    keyMessages: [
      'Soft offer, not pushy',
      "Clear pricing and what's included",
      'Emphasize they can DIY or get help',
    ],
    points: [
      'Alright, so you have two options: Set this up yourself using what I just showed you, or have us do it for you.',
      "If you want to DIY, awesome. I'll drop some free templates and resources in the chat. You can literally pause this and go set it up.",
      "If you want done-for-you service, here's what we offer:",
      'Our Signature Package is $297 per month. We set up and manage everything: auto-responder system, booking calendar integration, white-label CRM branded for your business.',
      'All your lead sources get connected in one place. You get ongoing support and optimization. We handle the tech, you focus on your business.',
      "There's normally a $250 setup fee, but for webinar attendees, we're waiving that. So you just pay the monthly fee.",
      'Separately, if you want Yelp ads management, we charge a 10% retainer fee on your ad spend. As a Yelp Advertising Partner, we can get you discounts and free upgrades if you qualify.',
      "We also offer additional services: review generation campaigns, website design, social media setup and optimization - we'll do up to 3 profiles like Google, Instagram, Facebook. We also do local SEO.",
      'These are all à la carte. You pick what you need. We can put together a custom package.',
      "Here's what I recommend: Download the free setup guide. Try setting it up yourself. If you get stuck or realize you don't have time, book a free consultation with us.",
      "We'll look at your specific situation and see if we can help. No pressure. No hard sell. Just a conversation.",
      'The link for the free guide and the consultation booking is in the chat.',
      "Now let's open it up for your questions!",
    ],
  },
  {
    slideId: 11,
    timing: '5-10 minutes',
    keyMessages: [
      'Open, welcoming energy',
      'Address questions directly and honestly',
      'Offer to continue conversation after',
    ],
    points: [
      "Alright, this is your time. What questions do you have? Drop them in the chat and let's talk through your specific situation.",
      '[Address questions as they come in - be conversational and helpful]',
      'Common questions to be ready for:',
      '- "How long does setup take?" → DIY: 2-4 hours spread over a week. Done-for-you: We handle it in 7-10 days.',
      '- "What if I don\'t have many leads yet?" → Perfect time to set this up BEFORE you scale. Get the foundation right.',
      '- "Can this work for [specific industry]?" → Yes, we\'ve done this for plumbers, HVAC, electricians, landscapers, lawyers, dentists, contractors.',
      '- "What\'s the ROI?" → TFP Landscaping added $31K in 90 days. Most clients see 5-10x ROI in first 6 months.',
      '- "Is it hard to learn?" → The CRM takes about 30 minutes to learn. We provide training and support.',
      '- "What about Yelp ads?" → Separate service. We can help if you want more leads, but the core system works with your existing lead sources.',
      "If we run out of time, I'll stick around for a few minutes after. You can also book a free consultation to talk through your specific situation.",
      'Thanks so much for being here and being engaged today. I really appreciate it!',
    ],
  },
]
